/**
 * Unlisted self-serve sign-up door (CLA-1032). Nothing links here — the URL is
 * handed out directly — so the route is `noindex` (see `signUpHead`) and lands
 * the brand-new account on the begin step, which creates customer + intake.
 *
 * @since 1.0.0
 */
import { SignUp } from "@clerk/tanstack-react-start";
import { useAtomValue } from "@effect-atom/atom-react";
import { createFileRoute } from "@tanstack/react-router";
import type { ComponentProps } from "react";
import { clawInstallIdentityAtom } from "~/lib/atoms/brand-identity.ts";
import { clawInstallModeAtom } from "~/lib/atoms/clawinstall-theme.ts";
import { signUpHead, validateLoginSearch } from "~/lib/login-search.ts";
import { SELF_SERVE_BEGIN_PATH } from "~/lib/self-serve-paths.ts";
import { signInAppearance } from "../login/index.tsx";

export const Route = createFileRoute("/clawinstall/sign-up/")({
  validateSearch: validateLoginSearch,
  head: () => signUpHead("ClawInstall"),
  component: ClawInstallSignUp,
});

/**
 * The sign-up card, wearing the same wrapper/monogram chrome as the login page
 * so the two doors are visually identical apart from the Clerk widget.
 *
 * @since 1.0.0
 */
export function ClawInstallSignUp() {
  const identity = useAtomValue(clawInstallIdentityAtom);
  const mode = useAtomValue(clawInstallModeAtom);
  const appearance: ComponentProps<typeof SignUp>["appearance"] = signInAppearance(mode);
  return (
    <div className="theme-clawinstall-customer flex min-h-screen items-center justify-center bg-surface px-6 font-sans text-on-surface">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <span
            aria-hidden="true"
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#09090b] text-xl font-bold leading-none text-white"
          >
            {identity.monogram}
          </span>
          <h1 className="font-headline-md text-headline-md tracking-tighter text-on-surface">
            {identity.displayName}
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant">Create account</p>
        </div>
        <SignUp
          routing="path"
          path="/sign-up"
          fallbackRedirectUrl={SELF_SERVE_BEGIN_PATH}
          signInUrl="/login"
          appearance={appearance}
        />
      </div>
    </div>
  );
}
